import axios from "axios";

import { Movement, SourceAttribute } from "@/hooks/useMovementsTable";
import { generateFilters } from "@/pages/movements/components/utils";

import { Match, MovementDetail, Step } from "../type";

export interface Transaction {
  id: string;
  date: number;
  counterparty: string;
  description: string;
  currency: string;
  amount: number;
}
export enum DataStatus {
  stateless = "stateless",
  excluded = "excluded",
  suggested = "suggested",
  reconciled = "reconciled",
  flagged = "flagged",
  recommended = "recommended",
}
export interface MovementData {
  id: string;
  date: string;
  counterpartyId?: string;
  counterpartyName?: string;
  counterpartyCode?: string;
  amount: number;
  description: string | null;
  group_id: number;
  id_match: string;
  source_id: string | null;
  source_type: string;
  status: string;
  currency: string;
  invoice: number | null;
  autoincremental_id: number;
  short_name: string;
}

export interface PaginatedData<R> {
  data: R[];
  pagination: {
    page: number;
    pageSize: number;
    totalPages: number;
    currentPage: number;
    nextPage: number | null;
    previousPage: number | null;
    totalItems: number;
  };
  status: string;
}

export interface Filter {
  field: string;
  value?: string;
  startValue?: string;
  endValue?: string;
  operator: string;
}

export interface MovementsBody {
  orderBy?: Array<[string, string]>;
  filters?: {
    [key: string]: Filter[];
  };
}

export const getMovements = async (
  searchParams: URLSearchParams,
  filters: { [key: string]: string },
) => {
  const body: MovementsBody = {};
  const sortBy = searchParams.get("sortby");
  const sortType = searchParams.get("sorttype");

  if (sortBy && sortType) {
    body.orderBy = [[sortBy, sortType]];
    searchParams.delete("sortby");
    searchParams.delete("sorttype");
  }

  if (Object.entries(filters).length > 0) {
    body.filters = {};
    const and: Filter[] = generateFilters(filters);
    body.filters.and = and;
  }

  const stringParams = searchParams.toString();
  const url = `/movement?${stringParams}`;

  const { data } = await axios.post<PaginatedData<MovementData>>(url, body);

  return data;
};

export interface UploadMovementsParams {
  files: File;
  sourceId: string;
}

export interface UploadMovementsResponse {
  status: string;
  data: UploadMovementsData;
  errorCode?: string;
  errorMessage?: string;
}

export interface UploadMovementsData {
  idFile: string;
  sourceId: string;
  tenantId: string;
  movementsPreview: Movement[];
  sourceCols: SourceAttribute[];
}

export interface MovementsPreview {
  [key: string]: string | null;
}

export const uploadMovements = async ({
  files,
  sourceId,
}: UploadMovementsParams) => {
  const formData = new FormData();
  formData.append("files", files);
  formData.append("sourceId", sourceId);

  const { data } = await axios.post<UploadMovementsResponse>(
    `/file/upload-movements`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return data;
};

export interface FileAckParams {
  idFile: string;
  sourceId: string;
}

export const fileAck = async ({ idFile, sourceId }: FileAckParams) => {
  const { data } = await axios.post(`/file/ack`, { idFile, sourceId });
  return data;
};

export interface DonwloadMovementsParams {
  startDate: string;
  endDate: string;
  movementType: string;
}

export async function downloadMovements({
  startDate,
  endDate,
  movementType,
}: DonwloadMovementsParams) {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    source_type: movementType,
  });
  const { data } = await axios.get(`/download-movements?${params.toString()}`);
  return data;
}

export interface DownloadMovementsWithFiltersParams {
  movementType: string;
  filters?: { [key: string]: string };
  orderBy?: [string, string][];
  group: string;
}

export async function downloadMovementsWithFilters({
  movementType,
  filters,
  orderBy,
  group,
}: DownloadMovementsWithFiltersParams) {
  const params = new URLSearchParams({ source_type: movementType, group });
  const body = {
    orderBy,
    filters: {
      and: Object.entries(filters ?? {}).map(([field, value]) => {
        if (field === "date") {
          const [startValue, endValue] = value.split("-");
          return {
            field,
            startValue: startValue.split(/\//g).reverse().join("-"),
            endValue: endValue.split(/\//g).reverse().join("-"),
            operator: "between",
          };
        }
        return {
          field,
          value,
          operator: "like",
        };
      }),
    },
  };

  const { data } = await axios.post(
    `/download-matches?${params.toString()}`,
    body,
  );

  return data;
}

export interface EngineExecuteParams {
  groups: string[];
}

export async function engineExecute({ groups }: EngineExecuteParams) {
  const { data } = await axios.post(`/general/engine/execute`, { groups });
  return data;
}

export interface UpdateCounterPartyParams {
  movementId: string;
  counterPartyId: string;
}

export async function updateCounterParty(params: UpdateCounterPartyParams) {
  try {
    const { data } = await axios.put(`/movement/counterparty`, {
      movement_id: params.movementId,
      counterparty_id: params.counterPartyId,
    });
    return data;
  } catch (error) {
    console.error(error);
    throw new Error("Error al actualizar la contraparte");
  }
}

export interface GetStepByMovementIdV2Params {
  groupId: string;
  stepId: string;
}

export interface GetStepByMovementIdResponse {
  status: string;
  step: Step;
  match?: Match;
}

export async function getStepByMovementIdV2(
  movementId: string,
  body: GetStepByMovementIdV2Params,
) {
  const { data } = await axios.post<GetStepByMovementIdResponse>(
    `/match/movement/${movementId}/step-v2`,
    body,
  );
  return data;
}

export interface GetMovementByIdResponse {
  status: string;
  data: {
    movement: MovementDetail;
  }[];
}

export async function getMovementById(movementId: string) {
  const { data } = await axios.get<GetMovementByIdResponse>(
    `/match/movement/${movementId}`,
  );
  return data;
}

export interface UpdateMovementFieldParams {
  movementId: string;
  fieldId: string;
  value: string;
}

export async function updateMovementField({
  fieldId,
  value,
  movementId,
}: UpdateMovementFieldParams) {
  const { data } = await axios.put(`/match/movement/${movementId}/field`, {
    fieldId,
    value,
  });
  return data;
}

export interface UpdateManyMovementsParams {
  movementIds: string[];
  subsourceId: string;
}

export async function updateManyMovements({
  movementIds,
  subsourceId,
}: UpdateManyMovementsParams) {
  const { data } = await axios.put(`/match/movement/update-many`, {
    movementIds,
    subsourceId,
  });
  return data;
}
